// client/src/components/PlanC/ProfessionalContextForm/index.tsx
//
// TRIL-116: shared form for Plan C surfaces. Required section in v1 (Task 10).
// Optional section added in Task 11. Submit wiring + analytics in Task 12.

import * as Select from '@radix-ui/react-select';
import { ChevronDown } from 'lucide-react';
import * as React from 'react';
import { trackEvent } from '../../../helpers/analytics';
import { EXPERTISE_AREA_TAXONOMY } from '../../../helpers/expertiseAreaTaxonomy';
import { humanize } from '../../../helpers/interestsTaxonomy';
import { removeTypenames } from '../../../helpers/removeTypenameFromPayload';
import { usePlanCScoreUplift } from '../../../hooks/usePlanCScoreUplift';
import type {
  Credential,
  ProfessionalContext,
  ProfessionalContextInput,
} from '../../../interfaces/user.interface';
import { CancelConfirmDialog } from './CancelConfirmDialog';
import {
  CREDENTIAL_EXPIRY_YEAR_MAX,
  CREDENTIAL_YEAR_MAX,
  CredentialRow,
} from './CredentialRow';
import { ExpertiseAutocomplete } from './ExpertiseAutocomplete';
import { ProfessionalContextReadView } from './ProfessionalContextReadView';
import { SuggestExpertiseDialog } from './SuggestExpertiseDialog';
import './professional-context-form.scss';

interface Props {
  initialValue?: ProfessionalContext;
  onSubmit: (input: ProfessionalContextInput) => void | Promise<void>;
  /**
   * Legacy callback kept for backward compatibility with popup wrappers.
   * As of TRIL-124, the form no longer invokes this from its internal Cancel
   * button — Cancel now either silently reverts to read mode (clean state) or
   * opens the CancelConfirmDialog (dirty state). Popup callers handle their
   * own dismiss UI.
   */
  onCancel: () => void;
  entrySurface: 'prompt' | 'nudge' | 'settings' | 'direct_url';
}

type FormMode = 'read' | 'edit';

function hasAnyValue(value?: ProfessionalContext): boolean {
  if (!value) return false;
  return (
    (value.expertiseAreas?.length ?? 0) > 0 ||
    Boolean(value.educationLevel) ||
    Boolean(value.currentProgram) ||
    Boolean(value.fieldOfStudy) ||
    Boolean(value.institution) ||
    Boolean(value.jobTitle) ||
    Boolean(value.yearsExperience) ||
    (value.credentials?.length ?? 0) > 0
  );
}

function isValidCredentialUrl(link: string): boolean {
  try {
    const url = new URL(link);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

/** Returns a concise, field-specific error for a credential, or null if valid. */
export function describeCredentialError(c: Credential): string | null {
  if (!c.title.trim()) return 'Title is required.';
  if (!c.issuer.trim()) return 'Issuer is required.';
  if (
    !Number.isInteger(c.year) ||
    c.year < 1900 ||
    c.year > CREDENTIAL_YEAR_MAX
  ) {
    return `Issued year must be between 1900 and ${CREDENTIAL_YEAR_MAX}.`;
  }
  if (c.expiryYear != null) {
    if (!Number.isInteger(c.expiryYear) || c.expiryYear <= c.year) {
      return 'Expiry year must be after the issued year.';
    }
    if (c.expiryYear > CREDENTIAL_EXPIRY_YEAR_MAX) {
      return `Expiry year must be ${CREDENTIAL_EXPIRY_YEAR_MAX} or earlier.`;
    }
  }
  if (c.link != null && c.link.length > 0 && !isValidCredentialUrl(c.link)) {
    return 'Enter a valid URL (http or https).';
  }
  return null;
}

export function isValidCredential(c: Credential): boolean {
  return describeCredentialError(c) === null;
}

/** True once the user has put anything in a credential row. Gates both the
 * per-row error display and the disabled-Save reason so a freshly-added blank
 * row is not flagged before it is touched — and so the reason never promises a
 * "highlighted" field that isn't rendered. */
export function credentialRowHasContent(c: Credential): boolean {
  return (
    Boolean(c.title) ||
    Boolean(c.issuer) ||
    Boolean(c.year) ||
    c.expiryYear != null ||
    Boolean(c.link)
  );
}

const EDUCATION_OPTIONS = [
  { value: 'HIGH_SCHOOL', label: 'High School' },
  { value: 'BACHELORS', label: "Bachelor's" },
  { value: 'MASTERS', label: "Master's" },
  { value: 'PHD', label: 'PhD' },
  { value: 'PROFESSIONAL_DEGREE', label: 'Professional Degree' },
  { value: 'CURRENTLY_ENROLLED', label: 'Currently Enrolled' },
];

const PROGRAM_OPTIONS = [
  { value: 'BACHELORS', label: "Bachelor's" },
  { value: 'MASTERS', label: "Master's" },
  { value: 'PHD', label: 'PhD' },
  { value: 'PROFESSIONAL', label: 'Professional' },
];

const YEARS_OPTIONS = [
  { value: 'Y0_2', label: '0–2 years' },
  { value: 'Y3_5', label: '3–5 years' },
  { value: 'Y6_10', label: '6–10 years' },
  { value: 'Y11_20', label: '11–20 years' },
  { value: 'Y20_PLUS', label: '20+ years' },
];

export function ProfessionalContextForm({
  initialValue,
  onSubmit,
  onCancel,
  entrySurface,
}: Props): React.ReactElement {
  const [expertiseAreas, setExpertiseAreas] = React.useState<string[]>(
    initialValue?.expertiseAreas ?? [],
  );
  const [educationLevel, setEducationLevel] = React.useState<string>(
    initialValue?.educationLevel ?? '',
  );
  const [currentProgram, setCurrentProgram] = React.useState<string>(
    initialValue?.currentProgram ?? '',
  );
  const [fieldOfStudy, setFieldOfStudy] = React.useState<string>(
    initialValue?.fieldOfStudy ?? '',
  );
  const [institution, setInstitution] = React.useState<string>(
    initialValue?.institution ?? '',
  );
  const [jobTitle, setJobTitle] = React.useState<string>(
    initialValue?.jobTitle ?? '',
  );
  const [yearsExperience, setYearsExperience] = React.useState<string>(
    initialValue?.yearsExperience ?? '',
  );
  const [credentials, setCredentials] = React.useState<Credential[]>(
    initialValue?.credentials ?? [],
  );
  type SaveState = 'idle' | 'saving' | 'saved' | 'error';
  const [saveState, setSaveState] = React.useState<SaveState>('idle');
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [isDirty, setIsDirty] = React.useState(false);
  const wasEmptyOnMount = React.useRef(!hasAnyValue(initialValue));
  const [mode, setMode] = React.useState<FormMode>(
    wasEmptyOnMount.current ? 'edit' : 'read',
  );
  type FocusTarget = 'education' | 'experience' | 'credentials' | null;
  const [pendingFocusTarget, setPendingFocusTarget] =
    React.useState<FocusTarget>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = React.useState(false);
  const [suggestDialog, setSuggestDialog] = React.useState<{
    open: boolean;
    initialQuery: string;
  }>({
    open: false,
    initialQuery: '',
  });

  React.useEffect(() => {
    if (mode !== 'edit' || !pendingFocusTarget) return;
    const id = window.requestAnimationFrame(() => {
      if (pendingFocusTarget === 'credentials') {
        // Credentials section: focus the first row's title input.
        // If no rows exist, create one first (matching the "+ Add credential" behavior).
        if (credentials.length === 0) {
          const newRow: Credential = {
            id: crypto.randomUUID(),
            title: '',
            issuer: '',
            year: 0,
          };
          setCredentials([newRow]);
          // Mirror the "+ Add credential" button's analytics event so the
          // CTA-driven entry path is counted equally — the user got a row
          // from the read-mode "+ Add credentials" CTA, not the in-form button.
          trackEvent('plan_c_credential_added', { currentCount: 1 });
          // Focus will happen on the next paint after the row mounts.
          window.requestAnimationFrame(() => {
            const el = document.querySelector(
              `[data-credential-id="${newRow.id}"] input[aria-label="Credential title"]`,
            ) as HTMLInputElement | null;
            el?.focus();
          });
        } else {
          const el = document.querySelector(
            '[data-credential-id] input[aria-label="Credential title"]',
          ) as HTMLInputElement | null;
          el?.focus();
        }
      } else {
        let elementId: string | null = null;
        if (pendingFocusTarget === 'education') elementId = 'education-level';
        else if (pendingFocusTarget === 'experience') elementId = 'job-title';
        if (elementId) {
          const el = document.getElementById(elementId);
          el?.focus();
        }
      }
      setPendingFocusTarget(null);
    });
    return () => window.cancelAnimationFrame(id);
  }, [mode, pendingFocusTarget, credentials.length]);

  const mountedAt = React.useRef(Date.now());
  const fieldsTouched = React.useRef(new Set<string>());
  // Save-success → read-mode transition timer. Captured so we can clear on
  // unmount and avoid "update on unmounted component" warnings if the user
  // navigates away during the 2s post-save window.
  const saveTimeoutRef = React.useRef<number | null>(null);
  React.useEffect(() => {
    return () => {
      if (saveTimeoutRef.current != null) {
        window.clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);
  const { armUpliftObserver } = usePlanCScoreUplift();

  const recordTouch = (field: string): void => {
    fieldsTouched.current.add(field);
  };

  const markDirty = (): void => setIsDirty(true);

  React.useEffect(() => {
    trackEvent('plan_c.form.opened', { entry_surface: entrySurface });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const interestOptions = React.useMemo(
    () =>
      EXPERTISE_AREA_TAXONOMY.map((value) => ({
        value,
        label: humanize(value),
      })),
    [],
  );

  const isEnrolled = educationLevel === 'CURRENTLY_ENROLLED';
  const disabledReason: string | null = (() => {
    if (expertiseAreas.length < 2) return 'Select at least 2 expertise areas.';
    if (!educationLevel) return 'Select your education status.';
    if (isEnrolled && !currentProgram) return 'Add your current program.';
    const invalidCredentials = credentials.filter((c) => !isValidCredential(c));
    if (invalidCredentials.length > 0) {
      return invalidCredentials.some(credentialRowHasContent)
        ? 'Fix the highlighted credential.'
        : 'Complete the empty credential, or remove it.';
    }
    return null;
  })();
  const canSubmit = disabledReason === null;

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!canSubmit || saveState === 'saving') return;
    const input: ProfessionalContextInput = {
      expertiseAreas,
      educationLevel,
      ...(isEnrolled && currentProgram && { currentProgram }),
      ...(isEnrolled && fieldOfStudy && { fieldOfStudy }),
      ...(isEnrolled && institution && { institution }),
      ...(jobTitle && { jobTitle }),
      ...(yearsExperience && { yearsExperience }),
      // Apollo cache reads inject __typename on every cached object. The
      // CredentialInput type doesn't declare __typename, so any cached row
      // that round-trips through the form would fail mutation coercion.
      // removeTypenames strips it recursively before submission.
      credentials: removeTypenames(credentials),
    };

    trackEvent('plan_c.form.submitted', {
      fields_filled_count: Object.keys(input).filter((k) => {
        const v = (input as unknown as Record<string, unknown>)[k];
        return Array.isArray(v) ? v.length > 0 : Boolean(v);
      }).length,
      education_level: educationLevel,
      is_enrolled: isEnrolled,
      time_to_complete_seconds: Math.round(
        (Date.now() - mountedAt.current) / 1000,
      ),
    });

    setSaveState('saving');
    setSaveError(null);
    try {
      armUpliftObserver();
      await onSubmit(input);
      trackEvent('plan_c_edit_mode_saved', {
        credentialCount: credentials.length,
        hasExpertise: expertiseAreas.length > 0,
        hasEducation: Boolean(educationLevel),
        hasExperience: Boolean(jobTitle) || Boolean(yearsExperience),
      });
      setSaveState('saved');
      setIsDirty(false);
      // Clear any in-flight timer before scheduling a new one — guards
      // against a theoretical rapid-double-save race where the previous
      // handler hasn't fired yet.
      if (saveTimeoutRef.current != null) {
        window.clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = window.setTimeout(() => {
        setSaveState('idle');
        // Post-save: user now has a saved baseline, so subsequent Edit sessions
        // should render the Cancel button (per spec). Without this reset, a
        // first-time user who saves and re-enters edit mode would never see
        // Cancel because wasEmptyOnMount was captured from the original empty
        // mount.
        wasEmptyOnMount.current = false;
        setMode('read');
        saveTimeoutRef.current = null;
      }, 2000);
    } catch (err) {
      setSaveState('error');
      setSaveError(
        err instanceof Error ? err.message : 'Save failed. Please try again.',
      );
    }
  };

  // TRIL-124: Reset all form-state fields back to initialValue baseline.
  const resetFormStateToBaseline = (): void => {
    setExpertiseAreas(initialValue?.expertiseAreas ?? []);
    setEducationLevel(initialValue?.educationLevel ?? '');
    setCurrentProgram(initialValue?.currentProgram ?? '');
    setFieldOfStudy(initialValue?.fieldOfStudy ?? '');
    setInstitution(initialValue?.institution ?? '');
    setJobTitle(initialValue?.jobTitle ?? '');
    setYearsExperience(initialValue?.yearsExperience ?? '');
    setCredentials(initialValue?.credentials ?? []);
    setIsDirty(false);
  };

  // TRIL-124: The form no longer calls the legacy `onCancel` prop from its
  // internal Cancel button. Clean state silently reverts to read mode; dirty
  // state opens the confirm dialog.
  // The `plan_c.form.abandoned` event was originally designed for popup
  // surfaces (prompt / nudge), where dismissing the popup is the only signal
  // the user gave up on Plan C. On the settings page, Cancel just exits edit
  // mode — the user is still on the settings page and may try again later, so
  // counting it as "abandoned" would inflate the funnel. Gate it to popup
  // surfaces only.
  const isPopupSurface = entrySurface === 'prompt' || entrySurface === 'nudge';

  const handleCancel = (): void => {
    if (isDirty) {
      setCancelDialogOpen(true);
      return;
    }
    trackEvent('plan_c_edit_mode_cancelled', { wasDirty: false });
    if (isPopupSurface) {
      trackEvent('plan_c.form.abandoned', {
        entry_surface: entrySurface,
        last_visible_step: isEnrolled ? 'enrolled' : 'required',
        time_spent_seconds: Math.round((Date.now() - mountedAt.current) / 1000),
        fields_touched_count: fieldsTouched.current.size,
      });
    }
    resetFormStateToBaseline();
    setMode('read');
  };

  const handleDiscard = (): void => {
    trackEvent('plan_c_edit_mode_cancelled', { wasDirty: true });
    if (isPopupSurface) {
      trackEvent('plan_c.form.abandoned', {
        entry_surface: entrySurface,
        last_visible_step: isEnrolled ? 'enrolled' : 'required',
        time_spent_seconds: Math.round((Date.now() - mountedAt.current) / 1000),
        fields_touched_count: fieldsTouched.current.size,
      });
    }
    setCancelDialogOpen(false);
    resetFormStateToBaseline();
    setMode('read');
  };

  const handleKeepEditing = (): void => {
    setCancelDialogOpen(false);
  };

  const handleSuggestNew = (query: string): void => {
    setSuggestDialog({ open: true, initialQuery: query });
  };

  const handleSuggestSubmit = (suggestedValue: string): void => {
    trackEvent('plan_c_expertise_suggestion_submitted', {
      suggested_value_length: suggestedValue.length,
    });
    // The analytics shim strips free-text fields (per TRIL-116's PII set). Sending length
    // gives us taxonomy-demand signal without exposing the raw suggestion. Full-value
    // collection can be a follow-up if needed.
  };

  const handleEditSection = (
    section: 'expertise' | 'education' | 'experience' | 'credentials',
  ): void => {
    const sourceMap = {
      expertise: 'cta_expertise',
      education: 'cta_education',
      experience: 'cta_experience',
      credentials: 'cta_credentials',
    } as const;
    trackEvent('plan_c_edit_mode_entered', { source: sourceMap[section] });
    setMode('edit');
    if (
      section === 'education' ||
      section === 'experience' ||
      section === 'credentials'
    ) {
      setPendingFocusTarget(section);
    }
  };

  const handleClickEditButton = (): void => {
    trackEvent('plan_c_edit_mode_entered', { source: 'edit_button' });
    setMode('edit');
  };

  if (mode === 'read' && initialValue) {
    return (
      <div className="plan-c-form plan-c-form--read">
        <ProfessionalContextReadView
          value={initialValue}
          onEditSection={handleEditSection}
        />
        <div className="plan-c-form__actions">
          <button
            type="button"
            className="plan-c-form__button-primary"
            onClick={handleClickEditButton}
          >
            Edit
          </button>
        </div>
      </div>
    );
  }

  return (
    <form className="plan-c-form" onSubmit={handleSubmit}>
      <div className="plan-c-form__field">
        <label htmlFor="expertise-areas-search">Expertise areas</label>
        <p className="plan-c-form__hint">Select at least 2</p>
        <ExpertiseAutocomplete
          value={expertiseAreas}
          onChange={(next) => {
            recordTouch('expertiseAreas');
            markDirty();
            setExpertiseAreas(next);
          }}
          onSuggestNew={handleSuggestNew}
        />
      </div>

      <div className="plan-c-form__field">
        <label htmlFor="education-level">Education status</label>
        <Select.Root
          value={educationLevel}
          onValueChange={(v) => {
            recordTouch('educationLevel');
            markDirty();
            setEducationLevel(v);
          }}
        >
          <Select.Trigger id="education-level" className="plan-c-form__select">
            <Select.Value placeholder="Choose..." />
            <Select.Icon>
              <ChevronDown size={16} />
            </Select.Icon>
          </Select.Trigger>
          <Select.Portal>
            <Select.Content
              className="plan-c-form__select-content"
              position="popper"
              sideOffset={4}
            >
              <Select.Viewport>
                {EDUCATION_OPTIONS.map(({ value, label }) => (
                  <Select.Item
                    key={value}
                    value={value}
                    className="plan-c-form__select-item"
                  >
                    <Select.ItemText>{label}</Select.ItemText>
                  </Select.Item>
                ))}
              </Select.Viewport>
            </Select.Content>
          </Select.Portal>
        </Select.Root>
      </div>

      {isEnrolled && (
        <>
          <div className="plan-c-form__field">
            <label htmlFor="current-program">Current program</label>
            <Select.Root
              value={currentProgram}
              onValueChange={(v) => {
                recordTouch('currentProgram');
                markDirty();
                setCurrentProgram(v);
              }}
            >
              <Select.Trigger
                id="current-program"
                className="plan-c-form__select"
              >
                <Select.Value placeholder="Choose..." />
              </Select.Trigger>
              <Select.Portal>
                <Select.Content
                  className="plan-c-form__select-content"
                  position="popper"
                  sideOffset={4}
                >
                  <Select.Viewport>
                    {PROGRAM_OPTIONS.map(({ value, label }) => (
                      <Select.Item
                        key={value}
                        value={value}
                        className="plan-c-form__select-item"
                      >
                        <Select.ItemText>{label}</Select.ItemText>
                      </Select.Item>
                    ))}
                  </Select.Viewport>
                </Select.Content>
              </Select.Portal>
            </Select.Root>
          </div>

          <div className="plan-c-form__field">
            <label htmlFor="field-of-study">Field of study</label>
            <Select.Root
              value={fieldOfStudy}
              onValueChange={(v) => {
                recordTouch('fieldOfStudy');
                markDirty();
                setFieldOfStudy(v);
              }}
            >
              <Select.Trigger
                id="field-of-study"
                className="plan-c-form__select"
              >
                <Select.Value placeholder="Choose..." />
              </Select.Trigger>
              <Select.Portal>
                <Select.Content
                  className="plan-c-form__select-content"
                  position="popper"
                  sideOffset={4}
                >
                  <Select.Viewport>
                    {interestOptions.map(({ value, label }) => (
                      <Select.Item
                        key={value}
                        value={value}
                        className="plan-c-form__select-item"
                      >
                        <Select.ItemText>{label}</Select.ItemText>
                      </Select.Item>
                    ))}
                  </Select.Viewport>
                </Select.Content>
              </Select.Portal>
            </Select.Root>
          </div>

          <div className="plan-c-form__field">
            <label htmlFor="institution">Institution</label>
            <input
              id="institution"
              type="text"
              maxLength={100}
              value={institution}
              onChange={(e) => {
                recordTouch('institution');
                markDirty();
                setInstitution(e.target.value);
              }}
              className="plan-c-form__input"
            />
          </div>
        </>
      )}

      <>
        <div className="plan-c-form__field">
          <label htmlFor="job-title">Job title</label>
          <input
            id="job-title"
            type="text"
            maxLength={100}
            value={jobTitle}
            onChange={(e) => {
              recordTouch('jobTitle');
              markDirty();
              setJobTitle(e.target.value);
            }}
            className="plan-c-form__input"
          />
        </div>

        <div className="plan-c-form__field">
          <label htmlFor="years-experience">Years of experience</label>
          <Select.Root
            value={yearsExperience}
            onValueChange={(v) => {
              recordTouch('yearsExperience');
              markDirty();
              setYearsExperience(v);
            }}
          >
            <Select.Trigger
              id="years-experience"
              className="plan-c-form__select"
            >
              <Select.Value placeholder="Choose..." />
              <Select.Icon>
                <ChevronDown size={16} />
              </Select.Icon>
            </Select.Trigger>
            <Select.Portal>
              <Select.Content
                className="plan-c-form__select-content"
                position="popper"
                sideOffset={4}
              >
                <Select.Viewport>
                  {YEARS_OPTIONS.map(({ value, label }) => (
                    <Select.Item
                      key={value}
                      value={value}
                      className="plan-c-form__select-item"
                    >
                      <Select.ItemText>{label}</Select.ItemText>
                    </Select.Item>
                  ))}
                </Select.Viewport>
              </Select.Content>
            </Select.Portal>
          </Select.Root>
        </div>

        <div className="plan-c-form__field">
          <span className="plan-c-form__group-label">Credentials</span>
          <p className="plan-c-form__hint">
            Professional certifications, degrees, or other credentials.
          </p>
          <div className="plan-c-form__credentials">
            {credentials.map((cred, idx) => (
              <CredentialRow
                key={cred.id}
                value={cred}
                onChange={(next) => {
                  recordTouch('credentials');
                  markDirty();
                  setCredentials((prev) =>
                    prev.map((c, i) => (i === idx ? next : c)),
                  );
                }}
                onRemove={() => {
                  recordTouch('credentials');
                  markDirty();
                  const next = credentials.filter((_, i) => i !== idx);
                  trackEvent('plan_c_credential_removed', {
                    remainingCount: next.length,
                  });
                  setCredentials(next);
                }}
                error={
                  credentialRowHasContent(cred)
                    ? describeCredentialError(cred)
                    : null
                }
              />
            ))}
            {credentials.length < 15 ? (
              <button
                type="button"
                className="plan-c-form__credentials-add"
                onClick={() => {
                  recordTouch('credentials');
                  markDirty();
                  const newRow: Credential = {
                    id: crypto.randomUUID(),
                    title: '',
                    issuer: '',
                    year: 0,
                  };
                  const next = [...credentials, newRow];
                  trackEvent('plan_c_credential_added', {
                    currentCount: next.length,
                  });
                  setCredentials(next);
                  window.requestAnimationFrame(() => {
                    const row = document.querySelector(
                      `[data-credential-id="${newRow.id}"] input[aria-label="Credential title"]`,
                    ) as HTMLInputElement | null;
                    row?.focus();
                  });
                }}
              >
                + Add credential
              </button>
            ) : (
              <p className="plan-c-form__credentials-limit-hint">
                Maximum 15 credentials
              </p>
            )}
          </div>
        </div>
      </>

      <div className="plan-c-form__actions">
        {saveState === 'error' && saveError && (
          <p className="plan-c-form__error" role="alert">
            {saveError}
          </p>
        )}
        {disabledReason && saveState !== 'saving' && saveState !== 'saved' && (
          <p className="plan-c-form__disabled-reason" role="status">
            {disabledReason}
          </p>
        )}
        {!wasEmptyOnMount.current && (
          <button
            type="button"
            onClick={handleCancel}
            className="plan-c-form__button-secondary"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={
            !canSubmit ||
            saveState === 'saving' ||
            saveState === 'saved' ||
            (saveState === 'idle' && !isDirty)
          }
          className={`plan-c-form__button-primary plan-c-form__button-primary--${saveState}`}
        >
          {saveState === 'saving' && 'Saving…'}
          {saveState === 'saved' && 'Saved ✓'}
          {(saveState === 'idle' || saveState === 'error') && 'Save'}
        </button>
      </div>
      <CancelConfirmDialog
        open={cancelDialogOpen}
        onDiscard={handleDiscard}
        onKeepEditing={handleKeepEditing}
      />
      <SuggestExpertiseDialog
        open={suggestDialog.open}
        initialQuery={suggestDialog.initialQuery}
        onClose={() => setSuggestDialog({ open: false, initialQuery: '' })}
        onSubmit={handleSuggestSubmit}
      />
    </form>
  );
}
